import { STUDY_TOOLS } from '@/pages/learn/study/studyTools';
import cases from './mathExamples.json';
import assets from './studyExperienceAssets.json';
import {
  triangleExplorer,
  triangleNumber,
  type TriangleMode,
} from '@/pages/learn/study/triangleExplorerModel';
import { geometryExplorer, geometryNumber } from '@/pages/learn/study/geometryExplorerModel';
import type { GeometryShape } from '@/pages/learn/study/mathModels';
import { fractionExplorer, type FractionMode } from '@/pages/learn/study/fractionExplorerModel';
import {
  numericLearning,
  learningNumber,
  type NumericTool,
} from '@/pages/learn/study/numericLearningModel';
import {
  linearLearning,
  systemLearning,
  functionLearning,
} from '@/pages/learn/study/algebraLearningModel';
import type { WorkingRow } from '@/pages/learn/study/workingCanvas';
import type { StudyDocumentDetail } from '@/pages/learn/study/studyApi';

export const STUDY_DEMO_LEARNER = {
  name: 'Alex (fictional demo)',
  age: 15,
  year: 10,
  school: 'Rivergum Demo College (fictional)',
  date: '2026-10-17',
  version: 'study-maths-demo-2026-10-17-v1',
} as const;
export const STUDY_DEMO_CASES = cases.map((item, index) => ({
  ...item,
  inputs: Object.fromEntries(
    Object.entries(item.inputs).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  ),
  workingId: `a1b02026-1017-4010-8000-${String(index + 1).padStart(12, '0')}`,
  questionId: `b1b02026-1017-4010-8000-${String(index + 1).padStart(12, '0')}`,
}));
export type StudyDemoCase = (typeof STUDY_DEMO_CASES)[number];
export function demoToolPath(item: StudyDemoCase) {
  const tool = STUDY_TOOLS.find((row) => row.subjectKey === 'mathematics' && row.id === item.tool);
  if (!tool) throw new Error('Prepared example has no registered mathematics tool.');
  return `${tool.path}?year=${item.year}&example=${item.key}`;
}
export function demoWorkingResult(item: StudyDemoCase) {
  const d = item.inputs;
  if (item.kind === 'triangle') {
    const r = triangleExplorer(item.mode as TriangleMode, d);
    return {
      steps: r.steps,
      outcome: `Hypotenuse ${Number.isSafeInteger(r.answer) ? '=' : '≈'} ${triangleNumber(r.answer)} ${r.unit}`,
      check: r.check,
    };
  }
  if (item.kind === 'geometry') {
    const r = geometryExplorer(item.mode as GeometryShape, { first: d.first, second: d.second });
    return {
      steps: r.steps,
      outcome: `Volume ≈ ${geometryNumber(r.primary)} cm³`,
      check: r.exact ?? 'Keep length, area and volume units distinct.',
    };
  }
  if (item.kind === 'fraction') {
    const r = fractionExplorer(item.mode as FractionMode, d);
    return {
      steps: r.steps,
      outcome: `${r.primaryLabel}: ${r.primary}`,
      check: `Fraction ${r.fraction}; decimal ${r.decimal.exact ? '=' : '≈'} ${r.decimal.text}; percentage ${r.percentDecimal.exact ? '=' : '≈'} ${r.percentDecimal.text}%. Parentheses repeat.`,
    };
  }
  if (item.kind === 'numeric') {
    const r = numericLearning(item.numeric as NumericTool, item.mode, d);
    return {
      steps: r.steps,
      outcome: `${r.resultLabel} ${Number(learningNumber(r.result)) === r.result && item.numeric !== 'exponential' ? '=' : '≈'} ${learningNumber(r.result)}`,
      check: r.interpretation,
    };
  }
  if (item.kind === 'linear') {
    const r = linearLearning(d);
    return { steps: r.steps, outcome: r.result, check: r.interpretation };
  }
  if (item.kind === 'system') {
    const r = systemLearning(
      [0, 1].map((i) => ({ a: d[`a${i}`], b: d[`b${i}`], c: d[`c${i}`] })),
      'elimination',
      'y',
    );
    return { steps: r.steps, outcome: r.result, check: r.interpretation };
  }
  const r = functionLearning('quadratic', d);
  return {
    steps: r.steps,
    outcome: item.kind === 'quadratic' ? r.rootSummary : r.result,
    check: r.interpretation,
  };
}
export function demoWorkingRows(): Array<WorkingRow & { updated_at: string }> {
  return STUDY_DEMO_CASES.map((item, index) => {
    const r = demoWorkingResult(item);
    return {
      id: item.workingId,
      version: 1,
      locked: false,
      context: { kind: 'free', question_id: item.questionId },
      updated_at: `2026-10-${String(10 + Math.floor(index / 2)).padStart(2, '0')}T04:00:00.000Z`,
      content: {
        format_version: 1,
        height: 1400,
        background: 'grid',
        question: item.title,
        text: [
          `Prepared fictional example - ${STUDY_DEMO_LEARNER.name}. Not assessed.`,
          item.question,
          `Inputs: ${item.inputLabel}`,
          ...r.steps.map(
            (step, i) => `${i + 1}. ${step.title}\n${step.formula}\n${step.explanation}`,
          ),
          `Result: ${r.outcome}`,
          `Check: ${r.check}`,
          `My next comparison: ${item.reflection}`,
        ].join('\n\n'),
        strokes: [],
      },
    };
  });
}
export function demoDocuments(): StudyDocumentDetail[] {
  return assets.map((asset, index) => ({
    id: `c1b02026-1017-4010-8000-${String(index + 1).padStart(12, '0')}`,
    file_name: asset.file_name,
    display_name: asset.title,
    mime_type: 'application/pdf',
    size_bytes: asset.size_bytes,
    status: 'ready',
    classification_state: 'learner_confirmed',
    context_suggestion: null,
    term_id: 'example-term',
    subject: { subject_key: 'mathematics', display_name: 'Mathematics' },
    created_at: `2026-10-${index ? '16' : '10'}T01:00:00.000Z`,
    read_url: asset.url,
    read_url_expires_in: 3600,
    source_revision: STUDY_DEMO_LEARNER.version,
    thumbnail: { kind: 'pdf', url: asset.url, expires_in: 3600 },
    analysis: null,
  }));
}
